import React, { useEffect, useMemo, useState } from "react";
import PatientSearch, { type PatientResult } from "../doctor/PatientSearch";
import ClinicPrintHeader from "../print/ClinicPrintHeader";
import ClinicPrintFooter from "../print/ClinicPrintFooter";
import { apiGet, apiPost } from "../../services/api";
import { useToast } from "../common/ToastProvider";
import Button from "../common/Button";
import "./PharmacyLetters.css";

const letterOptions = [
  { key: "referral", label: "Referral Letter", subtitle: "Specialist referral" },
  { key: "certificate", label: "Medical Certificate", subtitle: "Certificate of examination" },
  { key: "insurance", label: "Insurance Claim Letter", subtitle: "Claim support letter" },
];

type LetterKey = "referral" | "certificate" | "insurance";

type InsuranceRow = { procedure: string; date: string; amount: string };

type LetterForm = {
  date: string;
  letterNo: string;
  patientName: string;
  patientId: string;
  age: string;
  gender: string;
  examinedOn: string;
  doctorName: string;
  hospital: string;
  department: string;
  subjectPatient: string;
  findings: string;
  diagnosis: string;
  treatment: string;
  restFrom: string;
  restTo: string;
  insuranceCompany: string;
  treatmentFrom: string;
  treatmentTo: string;
  insuranceRows: InsuranceRow[];
  totalAmount: string;
};

const emptyForm = (): LetterForm => ({
  date: new Date().toISOString().slice(0, 10),
  letterNo: "",
  patientName: "",
  patientId: "",
  age: "",
  gender: "",
  examinedOn: "",
  doctorName: "",
  hospital: "",
  department: "",
  subjectPatient: "",
  findings: "",
  diagnosis: "",
  treatment: "",
  restFrom: "",
  restTo: "",
  insuranceCompany: "",
  treatmentFrom: "",
  treatmentTo: "",
  insuranceRows: [
    { procedure: "", date: "", amount: "" },
    { procedure: "", date: "", amount: "" },
    { procedure: "", date: "", amount: "" },
  ],
  totalAmount: "",
});

const fillText = (value?: string) => (value && value.trim() ? value : "—");

export default function PharmacyLettersPanel() {
  const [active, setActive] = useState<LetterKey>("referral");
  const [selectedPatient, setSelectedPatient] = useState<{ patient_id: string | number; name?: string } | null>(null);
  const [draftId, setDraftId] = useState<number | null>(null);
  const [form, setForm] = useState<LetterForm>(() => emptyForm());
  const [saving, setSaving] = useState(false);
  
  const { showToast } = useToast();

  const current = letterOptions.find((opt) => opt.key === active) ?? letterOptions[0];

  const canPrint = useMemo(() => {
    if (active === "insurance") return true;
    return true;
  }, [active]);

  const loadLatest = async (letterType: LetterKey, patientId?: string | number | null) => {
    try {
      const pid = patientId ? String(patientId) : "";
      const url = pid
        ? `/api/pharmacy/clinic-letters/latest?letter_type=${encodeURIComponent(letterType)}&patient_id=${encodeURIComponent(pid)}`
        : `/api/pharmacy/clinic-letters/latest?letter_type=${encodeURIComponent(letterType)}`;
      const res = await apiGet(url);
      if (!res) {
        setDraftId(null);
        setForm((prev) => ({ ...emptyForm(), date: prev.date }));
        return;
      }
      const data = res as { id?: number; payload?: Partial<LetterForm> };
      setDraftId(Number(data.id || 0) || null);
      setForm({ ...emptyForm(), ...(data.payload || {}) });
    } catch {
      setDraftId(null);
    }
  };

  useEffect(() => {
    loadLatest(active, selectedPatient?.patient_id ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, selectedPatient?.patient_id]);

  const onPickPatient = (p: PatientResult) => {
    setSelectedPatient({ patient_id: p.patient_id, name: p.name });
    setForm((prev) => ({
      ...prev,
      patientName: p.name || prev.patientName,
      patientId: String(p.patient_id),
    }));
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      const payload = {
        id: draftId,
        letter_type: active,
        patient_id: selectedPatient ? String(selectedPatient.patient_id) : null,
        payload: form,
      };
      const res = await apiPost("/api/pharmacy/clinic-letters/save", payload);
      const out = res as { ok?: boolean; id?: number };
      if (out?.id) setDraftId(out.id);
      showToast({ message: "Letter draft saved successfully.", type: "success" });
    } catch (e) {
      console.error(e);
      showToast({ message: "Failed to save letter draft.", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mod-standalone-window" style={{ minHeight: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column', padding: '0', backgroundColor: '#f8fafc' }}>
      <div className="mod-window-header no-print" style={{ padding: '24px 32px 0 32px', borderBottom: 'none' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px', marginBottom: '20px' }}>
          <div>
            <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>✉️ Clinic Letters</h2>
            <p className="mod-text-muted" style={{ margin: '4px 0 0 0' }}>Fill details here, save, and print a complete letter.</p>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <Button variant="outline" onClick={() => loadLatest(active, selectedPatient?.patient_id ?? null)}>
              ↻ Reload
            </Button>
            <Button variant="primary" onClick={saveDraft} isLoading={saving}>
              💾 Save Draft
            </Button>
            <Button variant="secondary" onClick={() => window.print()} disabled={!canPrint}>
              🖨️ Print Letter
            </Button>
          </div>
        </div>

        <nav className="mod-sub-nav">
          {letterOptions.map((opt) => (
            <button
              key={opt.key}
              type="button"
              className={`mod-sub-nav-item ${active === opt.key ? "active" : ""}`}
              onClick={() => setActive(opt.key as LetterKey)}
              style={{ flex: 1, textAlign: 'center' }}
            >
              <div style={{ fontWeight: 600 }}>{opt.label}</div>
              <div style={{ fontSize: '0.75rem', color: active === opt.key ? '#3b82f6' : '#94a3b8', marginTop: '2px', fontWeight: 400 }}>{opt.subtitle}</div>
            </button>
          ))}
        </nav>
      </div>

      <div className="letter-editor no-print" style={{ padding: '24px 32px', display: 'flex', justifyContent: 'center' }}>
        <div className="mod-card" style={{ maxWidth: '800px', width: '100%', padding: '0', overflow: 'hidden' }}>
          <div style={{ background: '#f8fafc', padding: '16px 24px', borderBottom: '1px solid #e2e8f0' }}>
            <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#0f172a' }}>Fill Details</h3>
          </div>

          <div className="letter-editor-section">
            <div className="field-row">
              <label>Date</label>
              <input type="date" value={form.date} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))} />
            </div>
            <div className="field-row">
              <label>Letter No</label>
              <input value={form.letterNo} onChange={(e) => setForm((p) => ({ ...p, letterNo: e.target.value }))} placeholder="e.g. TL-001" />
            </div>
          </div>

          <div className="letter-editor-section">
            <div className="field-row">
              <label>Patient</label>
              <div className="patient-search-wrap" style={{ position: 'relative', zIndex: 10 }}>
                <PatientSearch onSelect={onPickPatient} />
              </div>
              {selectedPatient ? (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '6px 12px', borderRadius: '8px', fontSize: '0.85rem', color: '#166534', marginTop: '8px' }}>
                  Selected: <strong>{fillText(selectedPatient.name)}</strong> ({String(selectedPatient.patient_id)})
                  <Button variant="ghost" size="sm" onClick={() => setSelectedPatient(null)} style={{ color: '#dc2626', padding: '2px 6px', height: 'auto', minHeight: 'auto' }}>✕ Clear</Button>
                </div>
              ) : null}
            </div>
            <div className="grid-3">
              <div className="field-row">
                <label>Patient Name</label>
                <input value={form.patientName} onChange={(e) => setForm((p) => ({ ...p, patientName: e.target.value }))} />
              </div>
              <div className="field-row">
                <label>Age</label>
                <input value={form.age} onChange={(e) => setForm((p) => ({ ...p, age: e.target.value }))} />
              </div>
              <div className="field-row">
                <label>Gender</label>
                <input value={form.gender} onChange={(e) => setForm((p) => ({ ...p, gender: e.target.value }))} placeholder="M/F" />
              </div>
            </div>
          </div>

          {active === "referral" ? (
            <div className="letter-editor-section">
              <div className="grid-2">
                <div className="field-row">
                  <label>Doctor Name</label>
                  <input value={form.doctorName} onChange={(e) => setForm((p) => ({ ...p, doctorName: e.target.value }))} />
                </div>
                <div className="field-row">
                  <label>Department / Hospital</label>
                  <input value={form.hospital} onChange={(e) => setForm((p) => ({ ...p, hospital: e.target.value }))} />
                </div>
              </div>
              <div className="field-row">
                <label>Examined On</label>
                <input type="date" value={form.examinedOn} onChange={(e) => setForm((p) => ({ ...p, examinedOn: e.target.value }))} />
              </div>
              <div className="field-row">
                <label>Findings</label>
                <textarea value={form.findings} onChange={(e) => setForm((p) => ({ ...p, findings: e.target.value }))} rows={4} />
              </div>
              <div className="field-row">
                <label>Provisional Diagnosis</label>
                <input value={form.diagnosis} onChange={(e) => setForm((p) => ({ ...p, diagnosis: e.target.value }))} />
              </div>
              <div className="field-row">
                <label>Treatment Provided</label>
                <textarea value={form.treatment} onChange={(e) => setForm((p) => ({ ...p, treatment: e.target.value }))} rows={3} />
              </div>
            </div>
          ) : null}

          {active === "certificate" ? (
            <div className="letter-editor-section">
              <div className="field-row">
                <label>Examined On</label>
                <input type="date" value={form.examinedOn} onChange={(e) => setForm((p) => ({ ...p, examinedOn: e.target.value }))} />
              </div>
              <div className="field-row">
                <label>Diagnosis</label>
                <input value={form.diagnosis} onChange={(e) => setForm((p) => ({ ...p, diagnosis: e.target.value }))} />
              </div>
              <div className="grid-2">
                <div className="field-row">
                  <label>Rest From</label>
                  <input type="date" value={form.restFrom} onChange={(e) => setForm((p) => ({ ...p, restFrom: e.target.value }))} />
                </div>
                <div className="field-row">
                  <label>Rest To</label>
                  <input type="date" value={form.restTo} onChange={(e) => setForm((p) => ({ ...p, restTo: e.target.value }))} />
                </div>
              </div>
              <div className="field-row">
                <label>Treatment Provided</label>
                <textarea value={form.treatment} onChange={(e) => setForm((p) => ({ ...p, treatment: e.target.value }))} rows={3} />
              </div>
            </div>
          ) : null}

          {active === "insurance" ? (
            <div className="letter-editor-section">
              <div className="field-row">
                <label>Insurance Company</label>
                <input value={form.insuranceCompany} onChange={(e) => setForm((p) => ({ ...p, insuranceCompany: e.target.value }))} />
              </div>
              <div className="grid-2">
                <div className="field-row">
                  <label>Treatment From</label>
                  <input type="date" value={form.treatmentFrom} onChange={(e) => setForm((p) => ({ ...p, treatmentFrom: e.target.value }))} />
                </div>
                <div className="field-row">
                  <label>Treatment To</label>
                  <input type="date" value={form.treatmentTo} onChange={(e) => setForm((p) => ({ ...p, treatmentTo: e.target.value }))} />
                </div>
              </div>
              <div className="field-row">
                <label>Procedures</label>
                <div className="insurance-grid">
                  {form.insuranceRows.map((r, idx) => (
                    <div key={idx} className="insurance-row">
                      <input
                        value={r.procedure}
                        placeholder="Procedure"
                        onChange={(e) => setForm((p) => {
                          const next = p.insuranceRows.slice();
                          next[idx] = { ...next[idx], procedure: e.target.value };
                          return { ...p, insuranceRows: next };
                        })}
                      />
                      <input
                        type="date"
                        value={r.date}
                        onChange={(e) => setForm((p) => {
                          const next = p.insuranceRows.slice();
                          next[idx] = { ...next[idx], date: e.target.value };
                          return { ...p, insuranceRows: next };
                        })}
                      />
                      <input
                        value={r.amount}
                        placeholder="Amount"
                        onChange={(e) => setForm((p) => {
                          const next = p.insuranceRows.slice();
                          next[idx] = { ...next[idx], amount: e.target.value };
                          return { ...p, insuranceRows: next };
                        })}
                      />
                    </div>
                  ))}
                  <div style={{ marginTop: '12px' }}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setForm((p) => ({ ...p, insuranceRows: [...p.insuranceRows, { procedure: "", date: "", amount: "" }] }))}
                    >
                      + Add row
                    </Button>
                  </div>
                </div>
              </div>
              <div className="field-row">
                <label>Total Treatment Amount</label>
                <input value={form.totalAmount} onChange={(e) => setForm((p) => ({ ...p, totalAmount: e.target.value }))} />
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="letter-page">
        <div className="letter-watermark">Thaila Dental</div>
        <ClinicPrintHeader title={current.label} subtitle={current.subtitle} />

        <div className="letter-meta">
          <div className="meta-row">
            <span>Date</span>
            <strong>{fillText(form.date)}</strong>
          </div>
          <div className="meta-row">
            <span>Letter No</span>
            <strong>{fillText(form.letterNo)}</strong>
          </div>
        </div>

        {active === "referral" && (
          <div className="letter-body">
            <p>
              To,<br />
              Dr. {fillText(form.doctorName)}<br />
              Department / Hospital {fillText(form.hospital)}
            </p>
            <p>
              Subject: Referral for dental evaluation of {fillText(form.patientName)}
            </p>
            <p>
              This is to refer Mr/Ms {fillText(form.patientName)} (Age {fillText(form.age)}, Gender {fillText(form.gender)})
              who was examined on {fillText(form.examinedOn || form.date)}. The patient presents with the following findings:
            </p>
            <div className="filled-block">{fillText(form.findings)}</div>
            <p>
              Provisional diagnosis: {fillText(form.diagnosis)}
            </p>
            <p>
              Treatment provided so far:
            </p>
            <div className="filled-block">{fillText(form.treatment)}</div>
            <p>
              Kindly evaluate and provide the necessary care. Please share your findings with us.
            </p>
          </div>
        )}

        {active === "certificate" && (
          <div className="letter-body">
            <p>
              This is to certify that Mr/Ms {fillText(form.patientName)} (Age {fillText(form.age)}) was examined at our clinic on
              {fillText(form.examinedOn || form.date)} and is diagnosed with {fillText(form.diagnosis)}.
            </p>
            <p>
              The patient has been advised rest / limited activity from {fillText(form.restFrom)} to {fillText(form.restTo)}.
            </p>
            <p>
              Treatment provided:
            </p>
            <div className="filled-block">{fillText(form.treatment)}</div>
            <p>
              This certificate is issued on request for necessary purposes.
            </p>
          </div>
        )}

        {active === "insurance" && (
          <div className="letter-body">
            <p>
              To,<br />
              Claims Department {fillText(form.insuranceCompany)}<br />
              Insurance Company {fillText(form.insuranceCompany)}
            </p>
            <p>
              Subject: Claim support letter for dental treatment of {fillText(form.patientName)}
            </p>
            <p>
              This is to certify that the above patient received dental treatment at our clinic from {fillText(form.treatmentFrom)}
              to {fillText(form.treatmentTo)}. The procedures carried out are listed below:
            </p>
            <table className="letter-table">
              <thead>
                <tr>
                  <th>Procedure</th>
                  <th>Date</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {form.insuranceRows.map((r, idx) => (
                  <tr key={idx}>
                    <td>{fillText(r.procedure)}</td>
                    <td>{fillText(r.date)}</td>
                    <td>{fillText(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p>
              Total treatment amount: {fillText(form.totalAmount)}
            </p>
            <p>
              Attachments: Itemized bill, prescription, clinical notes (if required).
            </p>
          </div>
        )}

        <ClinicPrintFooter
          note="Authorized signature and clinic stamp required."
          showSignature
          signatureLabel="Authorized Signatory"
        />
      </div>
    </div>
  );
}
